import { IonicButton, IonicInput } from 'ionic-ui';
import PhoneNumberInput from '../../../../../../ui/PhoneNumberInput';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { WalletKyc } from '../../../../../../../schemas/wallet-kyc-schema';
import { IonIcon } from '@ionic/react';
import { add, checkmark, trashBin } from 'ionicons/icons';
import React, { useState } from 'react';
import { DirectorCard } from './DirectorCard';
import {
  Basic_Director_Info,
  Disco_Code_Options,
  Director_Form_Initial_Field_Values,
  Director_Roles_Options,
  Director_Security_info,
  generateDirectorFormTriggerArray,
} from '../../../../../../../data/walletSetup/storeDirectorInfo';
import getEighteenYearsAgo from '../../../../../../../utils/helperFunction/getEighteenYearsAgo';

export default function DirectorsDialog() {
  const { control, trigger, watch, formState } = useFormContext<WalletKyc>();
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'directors',
  });

  const onDone = async (idx: number) => {
    const triggerArray = generateDirectorFormTriggerArray(idx);
    const isValid = await trigger(triggerArray);
    if (isValid) {
      setEditingIdx(null);
    }
  };

  const onRemove = (idx: number) => {
    setEditingIdx(null);
    remove(idx);
  };

  const onAdd = () => {
    setEditingIdx(fields.length);
    append(Director_Form_Initial_Field_Values as any);
  };

  const onEdit = async (idx: number) => {
    let fieldToValidate: any[] = [];
    if (editingIdx) {
      fieldToValidate = generateDirectorFormTriggerArray(idx);
    }

    const isValid = await trigger(fieldToValidate);
    if (isValid) {
      setEditingIdx(idx);
    }
  };

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center justify-between gap-4">
        <h6 className="high-emphasis-text font-medium">
          Add Store Owner(s) Information
        </h6>
        <IonicButton
          disabled={Boolean(editingIdx)}
          className="w-fit  button-with-icon"
          variant="outline"
          size="xs"
          onClick={onAdd}
        >
          <IonIcon icon={add} />
          <span>add</span>
        </IonicButton>
      </div>
      <p className="medium-emphasis-text">
        Please add at least one store owner to continue with your application.
      </p>
      {fields.map((completeField, idx) => {
        const isEditing = editingIdx === idx;
        return (
          <React.Fragment key={completeField.id}>
            {isEditing ? (
              <div className="space-y-2 ">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
                  {/* BASIC INFORMATION */}
                  {Basic_Director_Info.map(({ label, name, placeholder }) => (
                    <Controller
                      name={`directors.${idx}.${name}` as any}
                      control={control}
                      render={({ field, fieldState }) => (
                        <div key={name}>
                          <IonicInput
                            {...field}
                            label={label}
                            placeholder={placeholder}
                            hint={fieldState.error?.message ?? ''}
                            hintClass={'text-destructive'}
                          />
                        </div>
                      )}
                    />
                  ))}
                  {/* PHONE NUMBER */}
                  <Controller
                    name={`directors.${idx}.phone_number`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <PhoneNumberInput
                          {...field}
                          label="phone number*"
                          size="lg"
                          placeholder="e.g. 0801 234 5678"
                          hint={fieldState.error?.message ?? ''}
                          hintClass={'text-destructive'}
                        />
                      </div>
                    )}
                  />
                  {/* DATE OF BIRTH */}
                  <Controller
                    name={`directors.${idx}.date_of_birth`}
                    control={control}
                    render={({ field, fieldState }) => {
                      const max = getEighteenYearsAgo();
                      return (
                        <IonicInput
                          {...field}
                          label="Date of Birth*"
                          placeholder="YY-MM-DD"
                          type="date"
                          value={field.value?.trim() || max}
                          hint={fieldState.error?.message}
                          hintClass="text-destructive"
                        />
                      );
                    }}
                  />
                  {/* ROLE */}
                  <Controller
                    name={`directors.${idx}.role`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <IonicInput
                          {...field}
                          label="role"
                          type="select"
                          ignorePortal={true}
                          options={Director_Roles_Options}
                          placeholder="store type"
                          hint={fieldState.error?.message ?? ''}
                          hintClass={'text-destructive'}
                        />
                      </div>
                    )}
                  />
                  {/* PERCENTAGE OWNERSHIP */}
                  <Controller
                    name={`directors.${idx}.percentage_ownership`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <IonicInput
                          {...field}
                          prefix="%"
                          type="number"
                          containerClass="quantity-counter"
                          label="percentage ownership"
                          placeholder="e.g 10 for 10%"
                          max={100}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const raw = event.target.value;
                            if (!raw) return field.onChange('');
                            const numValue = Number(raw);
                            if (numValue < 100) {
                              field.onChange(numValue); // pass the number to react-hook-form
                            } else field.onChange(100);
                          }}
                          hint={fieldState.error?.message}
                          hintClass={'text-destructive'}
                        />
                      </div>
                    )}
                  />
                  {/* SECURITY INFORMATION */}
                  {Director_Security_info.map(
                    ({ label, max, name, placeholder }) => (
                      <Controller
                        name={`directors.${idx}.${name}` as any}
                        control={control}
                        render={({ field, fieldState }) => (
                          <div key={name}>
                            <IonicInput
                              {...field}
                              label={label}
                              placeholder={placeholder}
                              hint={fieldState.error?.message ?? ''}
                              hintClass={'text-destructive'}
                              max={max ?? undefined}
                            />
                          </div>
                        )}
                      />
                    )
                  )}

                  {/* DISCO CODE */}
                  <Controller
                    name={`directors.${idx}.disco_code`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <IonicInput
                          {...field}
                          label="Disco code*"
                          type="select"
                          ignorePortal={true}
                          options={Disco_Code_Options}
                          placeholder="Disco code"
                          hint={fieldState.error?.message ?? ''}
                          hintClass={'text-destructive'}
                        />
                      </div>
                    )}
                  />
                </div>
                {/* ACTION BUTTON */}
                <div className="flex items-center gap-2">
                  <IonicButton
                    type="button"
                    onClick={() => onRemove(idx)}
                    size="xs"
                    variant="outline"
                    className="button-with-icon destructive-button"
                  >
                    <IonIcon icon={trashBin} />
                    <span className="max-sm:hidden">remove</span>
                  </IonicButton>
                  <IonicButton
                    size="xs"
                    type="button"
                    className="button-with-icon"
                    onClick={() => onDone(idx)}
                  >
                    <IonIcon icon={checkmark} />
                    <span className="max-sm:hidden">done</span>
                  </IonicButton>
                </div>
              </div>
            ) : (
              // FINAL CARD OVERVIEW
              <DirectorCard
                full_name={`${watch(`directors.${idx}.last_name`)} ${watch(`directors.${idx}.first_name`)} `}
                email={watch(`directors.${idx}.email`)}
                phone={watch(`directors.${idx}.phone_number`)}
                role={watch(`directors.${idx}.role`)}
                percentage_ownership={watch(
                  `directors.${idx}.percentage_ownership`
                )}
                bvn={watch(`directors.${idx}.bvn`)}
                nin={watch(`directors.${idx}.nin`)}
                onRemove={() => {
                  if (editingIdx) setEditingIdx(editingIdx - 1);
                  remove(idx);
                }}
                onEdit={() => {
                  onEdit(idx);
                }}
              />
            )}
          </React.Fragment>
        );
      })}
      {formState.errors.directors?.message && (
        <p className="text-red-500 px-4 py-1 bg-red-50 rounded-md border border-red-500 font-medium text-center">
          {formState.errors.directors?.message}
        </p>
      )}
    </div>
  );
}
